import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type NamedMeasurement = 'layout/header' | 'layout/content' | 'layout/sidebar'; // extendable for future measurements as needed but used to help constrain to known values that will be measured

type Measurement = DOMRect;

const MeasurementsContext = createContext<null | {
  get: (name: NamedMeasurement) => Measurement | null;
  set: (name: NamedMeasurement, value: Measurement) => void;
  clear: (name: NamedMeasurement) => void;
}>(null);

/**
 * Hook to access currently measured values in the registry.
 */
export function useMeasurements() {
  const context = useContext(MeasurementsContext);
  if (!context) {
    throw new Error(
      'useMeasurements must be used within a MeasurementsProvider',
    );
  }

  return context;
}

/** Gives back a `ref` to be bound to an element to be measured as `name`. */
export function useMeasureRef<T extends HTMLElement = HTMLElement>(
  name: NamedMeasurement,
) {
  const { set, clear } = useMeasurements();
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    set(name, element.getBoundingClientRect());

    // only really necessary if the element can resize in some way
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return; // really only expecting one entry, accounts for indexed access check

      set(name, entry.target.getBoundingClientRect());
    });

    observer.observe(element);

    // oxlint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      clear(name);
    };
  }, [name, set, clear]);

  return ref;
}

export default function MeasurementsProvider({ children }: PropsWithChildren) {
  // alternatively could be a map, but a simple object is sufficient
  const [measurements, setMeasurements] = useState<
    Partial<Record<NamedMeasurement, Measurement>>
  >({});

  /** Set a measurement `value` by `name`. */
  const set = useCallback((name: NamedMeasurement, value: Measurement) => {
    setMeasurements((prev) => ({ ...prev, [name]: value }));
  }, []);

  /**
   * Get a measurement `value` by `name`.
   * Returns `null` if no measurement exists for the given `name`.
   */
  const get = useCallback(
    (name: NamedMeasurement) => measurements[name] ?? null,
    [measurements],
  );

  const clear = useCallback((name: NamedMeasurement) => {
    setMeasurements((prev) => {
      const { [name]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const value = useMemo(() => ({ get, set, clear }), [get, set, clear]);

  return (
    <MeasurementsContext.Provider value={value}>
      {children}
    </MeasurementsContext.Provider>
  );
}
